// Propshaft's RAILS_ASSET_URL macro emits a root-relative digested path
// (/assets/logo-white-abc123.svg), so on its own it resolves against whatever
// origin the document is on — wrong once assets are served from the CDN. This
// prepends the origin Rails computed, which layouts/_head_specific publishes as
// a meta tag. Empty wherever no asset host is configured (development, test),
// which leaves the root-relative path intact.
//
//   import assetUrl from 'src/utils/assetUrl'
//   const logo = assetUrl(RAILS_ASSET_URL('/logo-white.svg'))
//
// Sprites are exempt, mirroring the asset_host proc in
// config/initializers/assets.rb: `<use href>` only resolves same-origin, so
// /assets/sprites/ never goes to the CDN. Keeping the rule on both sides means
// wrapping a sprite is redundant rather than a silent prod-only broken icon —
// the mistake would be invisible in development, where there's no asset host to
// prepend in the first place.
//
// Still must NOT be used by anything running in a host page (the embed script
// and friends): that document has no meta tag of ours, so the path would
// resolve against the partner's origin.

// An empty tag is the normal development/test state — no asset host is
// configured, so there's nothing to prepend. A *missing* tag is always a
// mistake, and one that hides: the root-relative path it falls back to resolves
// correctly on our own pages, so nothing looks wrong until the CDN is involved
// or the document belongs to somebody else. Say so once, and don't throw —
// these calls sit at module scope, so throwing would take the bundle with it.
const readAssetOrigin = () => {
  const meta = document.querySelector('meta[name="asset-origin"]')
  if (meta) return meta.getAttribute('content') || ''

  console.warn(
    '[assetUrl] No <meta name="asset-origin"> in this document, so asset ' +
    'paths will resolve against this page\'s origin instead of the CDN. ' +
    'Either the layout is missing `render "layouts/head_specific"`, or this ' +
    'bundle runs on a host page — embed scripts have no access to our meta ' +
    'tags and must not use assetUrl. See CLAUDE.md → Assets in JS.'
  )
  return ''
}

// Memoized, so the warning above fires at most once per document ('' is not
// nullish, so a missing tag doesn't re-trigger the lookup on every call).
let assetOrigin
const getAssetOrigin = () => assetOrigin ??= readAssetOrigin()

const assetUrl = path =>
  path.startsWith('/assets/sprites/') ? path : getAssetOrigin() + path

export default assetUrl
